import { Calendar } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import itLocale from "@fullcalendar/core/locales/it";
import $ from "jquery";
import { InCoreModal, InCoreModalAction, InCoreModalLayout } from "./modal.utils";
import { InCoreSwal2, InCoreSwal2Icon } from "./swal.utils";

export const InCoreCalendarWindowType = {
    "Modal": "Modal",
    "NewTab": "NewTab",
    "SameWindow": "SameWindow"
};

// Valori di default per vista iniziale e toolbar: usati come fallback quando il
// div del calendario non dichiara data-initialview / data-toolbar.
const DEFAULT_INITIAL_VIEW = "dayGridMonth";
const DEFAULT_HEADER_TOOLBAR = {
    left: "prev,next today",
    center: "title",
    right: "dayGridMonth,timeGridWeek,timeGridDay listWeek",
};

// Fascia oraria visibile nelle viste timeGrid. Default GENERICO = giornata intera (FC),
// così la utility resta neutra per tutti i calendari. Ogni calendario può restringerla
// via data-slotmintime / data-slotmaxtime sul <div> (es. Appuntamenti usa 09:00-19:00,
// con max 19:00 per mostrare l'etichetta delle 18:00, che è il limite superiore esclusivo).
const DEFAULT_SLOT_MIN_TIME = "00:00:00";
const DEFAULT_SLOT_MAX_TIME = "24:00:00";

// Chiude l'eventuale popover "+N altri" di FullCalendar (.fc-popover): cliccando un evento
// al suo interno il popover non si auto-chiude, restando sovrapposto al modal di Edit/Details.
// Si simula il click sul bottone di chiusura nativo per far pulire a FullCalendar anche il
// proprio stato interno. Esportata (ed esposta come global InCoreCalendarClosePopover in
// scripts/index.ts) così i calendari che sovrascrivono eventClick lato view possono riusarla.
export function closeMoreLinkPopover(): void {
    const closeBtn = document.querySelector<HTMLElement>(".fc-popover .fc-popover-close");
    closeBtn?.click();
}

/**
 * Automatically initializes FullCalendar instances for elements with the `data-calendar` attribute.
 */
export function initializeCalendars(): void {
    $(() => {
        $("div[data-calendar]").each(function () {
            const obj = $(this);
            const id = obj.attr("id")!;
            const name = obj.data("calendar") as string;
            (window as any)["calendar"] = inCoreCalendar(id, name);
        });
    });
}

/**
 * Creates a FullCalendar instance for a specific element.
 * @param {string} id - The ID of the calendar element.
 * @param {string} name - The name of the calendar.
 * @returns {FullCalendar.Calendar} - The initialized FullCalendar instance.
 */
export function inCoreCalendar(id: string, name: string): Calendar {
    const obj = $(`#${id}`);
    const controller = obj.attr("data-controller") ?? "CalendarEvent";
    const action = obj.attr("data-action") ?? undefined;
    const isByUser = obj.attr("data-byuser") === "true";
    const disabled = obj.attr("data-disabled") === "true";
    const readonly = obj.attr("data-readonly") === "true";
    const createParams = obj.attr("data-createParams") ?? null;
    const editParams = obj.attr("data-editParams") ?? null;
    const windowType = obj.attr('data-windowType') ?? InCoreCalendarWindowType.Modal;

    const initialView = obj.attr("data-initialview") ?? DEFAULT_INITIAL_VIEW;
    const toolbarAttr = obj.attr("data-toolbar");
    let headerToolbar: any = DEFAULT_HEADER_TOOLBAR;
    if (toolbarAttr) {
        try { headerToolbar = JSON.parse(toolbarAttr); }
        catch { headerToolbar = DEFAULT_HEADER_TOOLBAR; }   // JSON malformato -> default
    }

    const slotMinTime = obj.attr("data-slotmintime") ?? DEFAULT_SLOT_MIN_TIME;
    const slotMaxTime = obj.attr("data-slotmaxtime") ?? DEFAULT_SLOT_MAX_TIME;

    // Opzioni opt-in per calendario: di default OFF/neutre (comportamento standard FC),
    // così la utility resta generica. Appuntamenti le attiva sul proprio <div>.
    const showAllDay = obj.attr("data-allday") !== "false";   // nasconde la riga all-day se "false"
    const agendaHeader = obj.attr("data-dayheader") === "agenda"; // header due righe (giorno + numero)
    const fixedMonth = obj.attr("data-fixedmonth") === "true"; // Mese: celle ad altezza uniforme + "+N altri"

    // Opt-in: limita il numero di eventi affiancati nelle viste timeGrid; oltre il limite
    // FullCalendar mostra il link "+N altri" (popover). Assente => nessun limite (default FC).
    const eventMaxStackAttr = obj.attr("data-eventmaxstack");
    const eventMaxStack = eventMaxStackAttr ? parseInt(eventMaxStackAttr, 10) : undefined;

    const calendarParams = { name };

    return inCoreCalendarCustom(
        id,
        isByUser,
        controller,
        action,
        disabled,
        readonly,
        calendarParams,
        createParams,
        editParams,
        windowType,
        initialView,
        headerToolbar,
        slotMinTime,
        slotMaxTime,
        showAllDay,
        agendaHeader,
        fixedMonth,
        eventMaxStack
    );
}

/**
 * Creates a custom FullCalendar instance with advanced configuration.
 * @param {string} id - The ID of the calendar element.
 * @param {boolean} isByUser - Whether the calendar is filtered by user.
 * @param {string} controller - The controller name for event handling.
 * @param {string | undefined} action - The action name for event fetching.
 * @param {boolean} disabled - Whether the calendar is disabled.
 * @param {boolean} readonly - Whether the calendar is read-only.
 * @param {any} params - Additional parameters for event fetching.
 * @param {any} createParams - Parameters for creating events.
 * @param {any} editParams - Parameters for editing events.
 * @param [windowType=InCoreCalendarWindowType.Modal] - Parameters for type of window to open
 * @returns {FullCalendar.Calendar} - The initialized FullCalendar instance.
 */
export function inCoreCalendarCustom(
    id: string,
    isByUser: boolean,
    controller: string,
    action: string | undefined,
    disabled: boolean,
    readonly: boolean,
    params: any,
    createParams: any = null,
    editParams: any = null,
    windowType: any = InCoreCalendarWindowType.Modal,
    initialView: string = DEFAULT_INITIAL_VIEW,
    headerToolbar: any = DEFAULT_HEADER_TOOLBAR,
    slotMinTime: string = DEFAULT_SLOT_MIN_TIME,
    slotMaxTime: string = DEFAULT_SLOT_MAX_TIME,
    showAllDay: boolean = true,
    agendaHeader: boolean = false,
    fixedMonth: boolean = false,
    eventMaxStack: number | undefined = undefined
): Calendar {
    const calElement = document.getElementById(id)!;
    const method = action || (isByUser ? "GetEventsByUser" : "GetEvents");

    const calendar = new Calendar(calElement, {
        plugins: [dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
        eventSources: [
            {
                url: `/${controller}/${method}`,
                extraParams: () => params,
            },
        ],
        initialView,
        locale: itLocale,
        firstDay: 1,
        // Formato 24h "H:00" sull'asse orario: sempre attivo (formato italiano).
        slotLabelFormat: { hour: "numeric", minute: "2-digit", hour12: false },
        // Fascia oraria visibile (default giornata intera; override via data-slotmintime/maxtime).
        slotMinTime,
        slotMaxTime,
        // Riga "Tutto il giorno": mostrata di default; nascosta con data-allday="false".
        allDaySlot: showAllDay,
        // Intestazione colonne stile "agenda" (giorno settimana sopra, numero grande sotto),
        // attiva solo con data-dayheader="agenda" e solo nelle viste timeGrid; altrimenti
        // undefined => testo di default FullCalendar. weekday/numero derivano dalla data.
        dayHeaderContent: agendaHeader ? (arg) => {
            if (arg.view.type.startsWith("timeGrid")) {
                const weekday = arg.date.toLocaleDateString(itLocale.code, { weekday: "short" });
                const day = arg.date.getDate();
                return {
                    html: `<div class="fc-incore-dayhead">` +
                              `<span class="fc-incore-dayhead-weekday">${weekday}</span>` +
                              `<span class="fc-incore-dayhead-day">${day}</span>` +
                          `</div>`
                };
            }
            return arg.text;
        } : undefined,
        themeSystem: "bootstrap5",
        dayMaxEventRows: 2,
        dayMaxEvents: 2,
        // Opt-in (fixedMonth): vista Mese mostra al massimo 3 appuntamenti per giorno; oltre
        // il 3° compare il link "+N altri" (dayMaxEvents: 3). L'altezza UNIFORME delle celle è
        // forzata via CSS (.fc-daygrid-day-frame min-height in appuntamenti.css), perché
        // dayMaxEvents numerico limita il conteggio ma non l'altezza. Scoped a dayGridMonth.
        views: fixedMonth ? { dayGridMonth: { dayMaxEvents: 3 } } : undefined,
        // Opt-in (eventMaxStack): nelle viste timeGrid limita gli eventi affiancati nella stessa
        // fascia; oltre il limite FullCalendar mostra "+N altri" (moreLinkClick di default = popover).
        // Impostato solo quando definito, così i calendari che non lo usano restano sul default FC.
        ...(eventMaxStack ? { eventMaxStack } : {}),
        // Bottone custom OPT-IN "oggiGiorno": porta a OGGI nella vista Giorno (timeGridDay),
        // a prescindere dalla vista corrente (a differenza del bottone nativo "today", che
        // mantiene la vista). 
        customButtons: {
            oggiGiorno: {
                text: "Oggi",
                click: () => {
                    const cal = $(`#${id}`).data("calen");
                    if (cal) cal.changeView("timeGridDay", new Date());
                },
            },
        },
        headerToolbar,
        // Sincronizza lo stato "attivo" (pill verde) del bottone custom "oggiGiorno" con la
        // vista corrente: FullCalendar marca con .fc-button-active solo i bottoni-vista nativi,
        // non i customButtons. In vista Giorno (timeGridDay) il bottone va evidenziato come
        // Settimana/Mese. Opt-in: se il bottone non è in toolbar, querySelector torna null -> no-op.
        datesSet: (arg) => {
            const btn = calElement.querySelector(".fc-oggiGiorno-button");
            if (btn) {
                btn.classList.toggle("fc-button-active", arg.view.type === "timeGridDay");
            }
        },
        dateClick: (info) => {
            if (!disabled && !readonly) {
                const base = controller + "/" + InCoreModalAction.CREATE;
                const url = new URL(base, window.location.origin); // Resolves relative path

                url.searchParams.set("DataInizio", info.dateStr);
                url.searchParams.set("Calendario", params.name);

                if (createParams) {
                    url.searchParams.set("Params", JSON.stringify(createParams));
                }

                switch (windowType) {
                    case InCoreCalendarWindowType.Modal:
                        InCoreModal(
                            controller,
                            InCoreModalAction.CREATE,
                            new InCoreModalLayout().ButtonsBelow(),
                            undefined,
                            refreshCalendars,
                            undefined,
                            ["DataInizio",
                            info.dateStr,
                            "Calendario",
                            params.name,
                            "Params",
                            JSON.stringify(createParams)]
                        );
                        break;

                    case InCoreCalendarWindowType.NewTab:
                        window.open(url.toString(), "_blank");
                        break;

                    case InCoreCalendarWindowType.SameWindow:
                        window.open(url.toString(), "_self");
                        break;
                }
            }
        },
        eventClick: (eventClickInfo) => {
            closeMoreLinkPopover();
            const id = eventClickInfo.event.id;
            if (readonly) {
                switch (windowType) {
                    case InCoreCalendarWindowType.Modal:
                        InCoreModal(
                            controller,
                            InCoreModalAction.DETAILS,
                            new InCoreModalLayout().ButtonsBelow(),
                            id,
                            refreshCalendars,
                            undefined,
                            []
                        );
                        break;

                    case InCoreCalendarWindowType.NewTab:
                        window.open(controller + "/" + InCoreModalAction.DETAILS + "/" + id, "_blank");
                        break;

                    case InCoreCalendarWindowType.SameWindow:
                        window.open(controller + "/" + InCoreModalAction.DETAILS + "/" + id, "_self");
                        break;
                }
            } else if (!disabled && !readonly) {
                switch (windowType) {
                    case InCoreCalendarWindowType.Modal:
                        InCoreModal(
                            controller,
                            InCoreModalAction.EDIT,
                            new InCoreModalLayout().ButtonsBelow(),
                            id,
                            refreshCalendars,
                            undefined,
                            ["Params",
                            JSON.stringify(editParams)]
                        );
                        break;

                    case InCoreCalendarWindowType.NewTab:
                        window.open(controller + "/" + InCoreModalAction.EDIT + "/" + id, "_blank");
                        break;

                    case InCoreCalendarWindowType.SameWindow:
                        window.open(controller + "/" + InCoreModalAction.EDIT + "/" + id, "_self");
                        break;
                }
            }
        },
        eventResize: (info) => {
            if (!disabled && !readonly) {
                const id = info.event.id;
                const deltaI = info.startDelta;
                const deltaF = info.endDelta;
                const rev = info.revert;
                editEvent("ResizeEvent", id, controller, deltaI, deltaF, rev);
            } else {
                info.revert();
                InCoreSwal2(InCoreSwal2Icon.Error, "Operazione non disponibile");
            }
        },
        eventDrop: (eventDropInfo) => {
            if (!disabled && !readonly) {
                const id = eventDropInfo.event.id;
                const delta = eventDropInfo.delta;
                const rev = eventDropInfo.revert;
                editEvent("MoveEvent", id, controller, delta, undefined, rev);
            } else {
                eventDropInfo.revert();
                InCoreSwal2(InCoreSwal2Icon.Error, "Operazione non disponibile");
            }
        },
        // Contenuto evento (API ufficiale `eventContent`, gestita dal vdom di FullCalendar:
        // niente duplicati al cambio vista). La util resta GENERICA: il rendering specifico di
        // dominio (es. card Appuntamenti con avatar/icona/righe) è delegato a un renderer
        // registrato PER NOME calendario in `window.InCoreCalendarEventRenderers[name]`.
        // Se non è registrato (o ritorna un valore falsy) si usa il rendering di default:
        // card generica con orario + titolo (classe .calendarCard del tema InCore).
        eventContent: (arg) => {
            const renderer = (window as any).InCoreCalendarEventRenderers?.[params?.name];
            if (typeof renderer === "function") {
                const custom = renderer(arg);
                if (custom) {
                    return custom;
                }
            }

            const ev = arg.event;
            const start = ev.start?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) ?? "00:00";
            const end = ev.end?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) ?? "00:00";
            const classes = ev.classNames.join(' ');
            const style = `border-color:${ev.borderColor}; color:${ev.textColor}; background-color:${ev.backgroundColor};`;
            const ep: any = ev.extendedProps;
            const icona = Array.isArray(ep) ? ep.join('') : "";
            return {
                html:
                    `<div class="calendarCard ${classes}" style="${style}">` +
                        `<div>${start} - ${end}${icona}</div>` +
                        `<div>${ev.title}</div>` +
                    `</div>`
            };
        }
    });

    calendar.render();
    $(`#${id}`).data("calen", calendar);
    adjustCalendarHeight();

    // WORKAROUND: al primo caricamento la riga "Tutto il giorno" delle viste timeGrid resta alta
    // quanto un solo evento e taglia gli altri; calendar.updateSize() non basta, la corregge solo
    // la ricreazione della vista. Una volta sola, a eventi caricati (ultimo eventsSet + 300ms),
    // si passa da listWeek e si torna alla vista corrente. Causa vera non ancora individuata.
    if (showAllDay) {
        let vistaRicreata = false;
        let timerRicreaVista: number | undefined;

        calendar.on("eventsSet", () => {
            if (vistaRicreata) return;
            clearTimeout(timerRicreaVista);
            timerRicreaVista = window.setTimeout(() => {
                vistaRicreata = true;
                const vista = calendar.view.type;
                if (vista.startsWith("timeGrid")) {
                    calendar.changeView("listWeek");
                    calendar.changeView(vista);
                }
            }, 300);
        });
    }

    return calendar;
}

/**
 * Refreshes all FullCalendar instances on the page.
 */
export function refreshCalendars(): void {
    $("[data-calendar]").each((_, element) => {
        $(element).data("calen").refetchEvents();
    });
    adjustCalendarHeight();
}

/**
 * Edits an event by sending a POST request.
 * @param {string} func - The server-side function to call.
 * @param {string} id - The event ID.
 * @param {string} controller - The controller name.
 * @param {any} deltaI - The start delta.
 * @param {any} deltaF - The end delta.
 * @param {Function} rev - The revert function.
 */
export function editEvent(
    func: string,
    id: string,
    controller: string,
    deltaI: any,
    deltaF: any,
    rev: Function
): void {
    const data = deltaF
        ? { id, deltaInizio: deltaI, deltaFine: deltaF }
        : { id, delta: deltaI };

    $.ajax({
        type: "POST",
        url: `/${controller}/${func}`,
        data: data,
        success: (result) => {
            if (result !== "success") {
                rev();
                InCoreSwal2(InCoreSwal2Icon.Error, result);
            }
        },
        error: (result) => {
            rev();
            InCoreSwal2(InCoreSwal2Icon.Error, result.responseText);
        },
    });
}

/**
 * Adjusts the height of the calendar to fix layout issues.
 */
export function adjustCalendarHeight(): void {
    const body = $(".fc-daygrid-body");
    if (body.hasClass("fc-daygrid-body-unbalanced")) {
        body.removeClass("fc-daygrid-body-unbalanced").addClass("fc-daygrid-body-balanced");
    }
}